import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/AdminCrud";

export default function AdminHomepage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["homepage"], queryFn: () => api.get("/homepage").then((r) => r.data) });
  const [form, setForm] = useState(null);
  const s = form ?? data ?? {};
  const setF = (k, v) => setForm({ ...(form ?? data ?? {}), [k]: v });
  const setAbout = (k, v) => setForm({ ...(form ?? data ?? {}), about: { ...(s.about || {}), [k]: v } });
  const setListItem = (key, idx, field, v) => {
    const list = [...(s[key] || [])];
    list[idx] = { ...list[idx], [field]: v };
    setF(key, list);
  };
  const addListItem = (key, template) => setF(key, [...(s[key] || []), template]);
  const removeListItem = (key, idx) => setF(key, (s[key] || []).filter((_, i) => i !== idx));

  const save = useMutation({
    mutationFn: (payload) => api.put("/admin/homepage", payload).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["homepage"] }); toast.success("Homepage saved."); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || "Save failed"),
  });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.24em] text-amber-600">Manage</div>
        <h1 className="font-display text-3xl text-slate-950 mt-1">Homepage CMS</h1>
      </div>

      <Card className="border border-slate-200"><CardContent className="p-6 space-y-4">
        <div className="text-sm font-medium text-slate-700">About</div>
        <div><Label>Title</Label><Input value={s.about?.title || ""} onChange={(e) => setAbout("title", e.target.value)} data-testid="about-title" /></div>
        <div><Label>Body</Label><Textarea rows={4} value={s.about?.body || ""} onChange={(e) => setAbout("body", e.target.value)} data-testid="about-body" /></div>
        <div><Label>Image</Label><ImageUpload value={s.about?.image_url} onChange={(v) => setAbout("image_url", v)} testId="about-image" /></div>
      </CardContent></Card>

      <Card className="border border-slate-200"><CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between"><div className="text-sm font-medium text-slate-700">Why Choose Us</div><Button variant="outline" size="sm" onClick={() => addListItem("why_choose_us", { icon: "sparkles", title: "", body: "" })} data-testid="add-why">+ Add</Button></div>
        {(s.why_choose_us || []).map((it, i) => (
          <div key={i} className="grid md:grid-cols-4 gap-3 border p-3 rounded-md">
            <Input placeholder="icon" value={it.icon || ""} onChange={(e) => setListItem("why_choose_us", i, "icon", e.target.value)} data-testid={`why-icon-${i}`} />
            <Input placeholder="title" value={it.title || ""} onChange={(e) => setListItem("why_choose_us", i, "title", e.target.value)} data-testid={`why-title-${i}`} />
            <Input placeholder="body" value={it.body || ""} onChange={(e) => setListItem("why_choose_us", i, "body", e.target.value)} className="md:col-span-2" data-testid={`why-body-${i}`} />
            <Button variant="outline" size="sm" onClick={() => removeListItem("why_choose_us", i)} className="md:col-span-4 md:justify-self-end text-red-600">Remove</Button>
          </div>
        ))}
      </CardContent></Card>

      <Card className="border border-slate-200"><CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between"><div className="text-sm font-medium text-slate-700">Stats</div><Button variant="outline" size="sm" onClick={() => addListItem("stats", { label: "", value: "" })} data-testid="add-stat">+ Add</Button></div>
        {(s.stats || []).map((st, i) => (
          <div key={i} className="grid md:grid-cols-3 gap-3 border p-3 rounded-md">
            <Input placeholder="label" value={st.label || ""} onChange={(e) => setListItem("stats", i, "label", e.target.value)} data-testid={`stat-label-${i}`} />
            <Input placeholder="value" value={st.value || ""} onChange={(e) => setListItem("stats", i, "value", e.target.value)} data-testid={`stat-value-${i}`} />
            <Button variant="outline" size="sm" onClick={() => removeListItem("stats", i)} className="text-red-600">Remove</Button>
          </div>
        ))}
      </CardContent></Card>

      <div className="flex justify-end">
        <Button className="bg-slate-950 hover:bg-slate-800" disabled={save.isPending} onClick={() => save.mutate(form || data || {})} data-testid="homepage-save-btn">{save.isPending ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}
